import { useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Wireframe globe with animated trade arcs and pulsing endpoints.
 * Performant: only TubeGeometries created once, materials reused.
 */

// Trade hub coordinates (approx lat, lon)
const HUBS = [
    { id: "kl", name: "Kuala Lumpur", lat: 3.1, lon: 101.7 },
    { id: "sg", name: "Singapore", lat: 1.35, lon: 103.8 },
    { id: "sh", name: "Shanghai", lat: 31.2, lon: 121.5 },
    { id: "dxb", name: "Dubai", lat: 25.2, lon: 55.3 },
    { id: "syd", name: "Sydney", lat: -33.9, lon: 151.2 },
    { id: "yyz", name: "Toronto", lat: 43.7, lon: -79.4 },
    { id: "ist", name: "Istanbul", lat: 41.0, lon: 28.9 },
    { id: "doh", name: "Doha", lat: 25.3, lon: 51.5 },
];

const CONNECTIONS = [
    ["kl", "sg"],
    ["kl", "sh"],
    ["kl", "dxb"],
    ["kl", "syd"],
    ["kl", "yyz"],
    ["kl", "ist"],
    ["dxb", "ist"],
    ["dxb", "doh"],
    ["sh", "syd"],
];

const RADIUS = 1.6;

function latLonToVec3(lat, lon, r = RADIUS) {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);
    const x = -r * Math.sin(phi) * Math.cos(theta);
    const z = r * Math.sin(phi) * Math.sin(theta);
    const y = r * Math.cos(phi);
    return new THREE.Vector3(x, y, z);
}

function buildArc(a, b, lift = 0.45) {
    const start = latLonToVec3(a.lat, a.lon);
    const end = latLonToVec3(b.lat, b.lon);
    const mid = start
        .clone()
        .add(end)
        .multiplyScalar(0.5)
        .normalize()
        .multiplyScalar(RADIUS + lift + start.distanceTo(end) * 0.15);
    return new THREE.QuadraticBezierCurve3(start, mid, end);
}

export function Globe({ rotationRef }) {
    const groupRef = useRef();
    const wireRef = useRef();

    // Load real-Earth textures (NASA-derived) — hosted locally to avoid CORS.
    const [earthMap, earthNormal, earthSpec] = useLoader(THREE.TextureLoader, [
        "/textures/earth_color.jpg",
        "/textures/earth_normal.jpg",
        "/textures/earth_specular.jpg",
    ]);

    useMemo(() => {
        [earthMap, earthNormal, earthSpec].forEach((tex) => {
            if (!tex) return;
            tex.anisotropy = 4;
        });
        if (earthMap) earthMap.colorSpace = THREE.SRGBColorSpace;
        if (earthNormal) earthNormal.colorSpace = THREE.NoColorSpace;
        if (earthSpec) earthSpec.colorSpace = THREE.NoColorSpace;
    }, [earthMap, earthNormal, earthSpec]);

    // Geometry: low-poly icosahedron-based sphere wireframe
    const sphereGeo = useMemo(
        () => new THREE.IcosahedronGeometry(RADIUS, 4),
        [],
    );
    const wireGeo = useMemo(
        () => new THREE.WireframeGeometry(sphereGeo),
        [sphereGeo],
    );
    const innerGeo = useMemo(
        () => new THREE.SphereGeometry(RADIUS * 0.985, 48, 48),
        [],
    );

    // Latitude/longitude rings
    const latLonLines = useMemo(() => {
        const lines = [];
        // Latitudes
        for (let i = 1; i < 8; i++) {
            const lat = -90 + (180 / 8) * i;
            const pts = [];
            for (let j = 0; j <= 64; j++) {
                pts.push(latLonToVec3(lat, (j / 64) * 360 - 180, RADIUS));
            }
            lines.push(new THREE.BufferGeometry().setFromPoints(pts));
        }
        // Longitudes
        for (let i = 0; i < 12; i++) {
            const lon = -180 + (360 / 12) * i;
            const pts = [];
            for (let j = 0; j <= 64; j++) {
                pts.push(latLonToVec3((j / 64) * 180 - 90, lon, RADIUS));
            }
            lines.push(new THREE.BufferGeometry().setFromPoints(pts));
        }
        return lines;
    }, []);

    // Arcs
    const arcs = useMemo(() => {
        const byId = Object.fromEntries(HUBS.map((h) => [h.id, h]));
        return CONNECTIONS.map(([a, b], i) => {
            const curve = buildArc(byId[a], byId[b]);
            const tube = new THREE.TubeGeometry(curve, 64, 0.012, 8, false);
            const points = curve.getPoints(64);
            const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
            return { tube, lineGeo, curve, key: `${a}-${b}-${i}` };
        });
    }, []);

    // Hub markers
    const markers = useMemo(
        () => HUBS.map((h) => ({ id: h.id, pos: latLonToVec3(h.lat, h.lon) })),
        [],
    );

    // Per-frame: rotate globe based on scroll-driven rotationRef
    useFrame((state, delta) => {
        const t = state.clock.elapsedTime;
        const targetY = rotationRef?.current ?? 0;
        if (groupRef.current) {
            // Smooth rotation toward scroll target
            groupRef.current.rotation.y +=
                (targetY - groupRef.current.rotation.y) * 0.05;
            groupRef.current.rotation.x = Math.sin(t * 0.1) * 0.05 - 0.15;
        }
        if (wireRef.current) {
            wireRef.current.material.opacity =
                0.04 + Math.sin(t * 0.6) * 0.02;
        }
    });

    return (
        <group ref={groupRef}>
            {/* Real Earth — textured sphere */}
            <mesh geometry={innerGeo}>
                <meshStandardMaterial
                    map={earthMap}
                    normalMap={earthNormal}
                    roughnessMap={earthSpec}
                    roughness={0.85}
                    metalness={0.05}
                    emissive="#0a1a2e"
                    emissiveIntensity={0.15}
                />
            </mesh>

            {/* Wireframe overlay — very subtle so Earth texture remains visible */}
            <lineSegments ref={wireRef} geometry={wireGeo}>
                <lineBasicMaterial
                    color="#ffffff"
                    transparent
                    opacity={0.05}
                />
            </lineSegments>

            {/* Lat / Lon rings — subtle white */}
            {latLonLines.map((g, i) => (
                <line key={`ll-${i}`} geometry={g}>
                    <lineBasicMaterial
                        color="#ffffff"
                        transparent
                        opacity={0.08}
                    />
                </line>
            ))}

            {/* Trade arcs */}
            {arcs.map((a, i) => (
                <group key={a.key}>
                    <mesh geometry={a.tube}>
                        <meshBasicMaterial
                            color="#D69331"
                            transparent
                            opacity={0.85}
                            toneMapped={false}
                        />
                    </mesh>
                    <ArcPulse curve={a.curve} index={i} />
                </group>
            ))}

            {/* Hub markers */}
            {markers.map((m) => (
                <group key={m.id} position={m.pos}>
                    <mesh>
                        <sphereGeometry args={[0.025, 16, 16]} />
                        <meshBasicMaterial
                            color="#D69331"
                            toneMapped={false}
                        />
                    </mesh>
                    <PulseRing />
                </group>
            ))}
        </group>
    );
}

function ArcPulse({ curve, index }) {
    const ref = useRef();
    useFrame((state) => {
        if (!ref.current) return;
        const t =
            ((state.clock.elapsedTime * 0.25 + index * 0.13) % 1 + 1) % 1;
        const p = curve.getPoint(t);
        ref.current.position.set(p.x, p.y, p.z);
    });
    return (
        <mesh ref={ref}>
            <sphereGeometry args={[0.022, 12, 12]} />
            <meshBasicMaterial color="#ffffff" toneMapped={false} />
        </mesh>
    );
}

function PulseRing() {
    const ref = useRef();
    useFrame((state) => {
        if (!ref.current) return;
        const t = (state.clock.elapsedTime * 0.8) % 1;
        const s = 0.5 + t * 4;
        ref.current.scale.set(s, s, s);
        ref.current.material.opacity = (1 - t) * 0.5;
    });
    return (
        <mesh ref={ref} rotation={[Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.04, 0.05, 24]} />
            <meshBasicMaterial
                color="#D69331"
                transparent
                opacity={0.5}
                side={THREE.DoubleSide}
                toneMapped={false}
            />
        </mesh>
    );
}
